/**
 * Pure phase-lock emit count. Returns how many particles to spawn for one emitter
 * during the step [tPrev, tNow].
 *
 * floor(t * rate + offset) is the cumulative emit count as a pure function of time —
 * emitters with identical (rate, offset) always fire on the same steps regardless of
 * when they were added. The per-step clamp prevents bursts when rate changes while
 * elapsed is large.
 */
export function computeEmitCount(
  rate: number,
  offset: number,
  tPrev: number,
  tNow: number,
): number {
  const raw = Math.floor(tNow * rate + offset) - Math.floor(tPrev * rate + offset);
  return Math.min(raw, Math.ceil(rate * (tNow - tPrev)) + 1);
}

/**
 * The look-back start of one fixed substep's emit window. Windows are half-open
 * (tPrev, tNow], so only the very first emit step after construction widens its
 * window below zero — otherwise the clock-origin boundary at t=0 (sequencer cell
 * 0, the first beat) would fall outside every window and never fire. Every later
 * step uses the true previous time, including the first step after reset() rewinds
 * the clock to 0, so a reset never replays the t=0 beat as a spurious burst.
 */
export function emitWindowStart(tNow: number, dt: number, firstEver: boolean): number {
  return firstEver ? -dt : tNow - dt;
}

/**
 * Effective free-Hz emit rate under the emitter's gate-param multiplier. The
 * gate is clamped to [0,1] so a driven value above 1 never exceeds the user-set
 * rate and a negative value stops emission. An undriven gate rests at 1,
 * leaving the rate untouched.
 */
export function gatedRate(rate: number, gate: number): number {
  return rate * Math.min(1, Math.max(0, gate));
}

/**
 * Phase-lock emit count for an emitter snapped to the room clock grid. It fires
 * every `gridMultiple` grid cells (period = gridMultiple * gridSec), so the firing
 * boundaries are a subset of the grid and always land on a beat. `offset` is the
 * same period-relative phase shift as the free-Hz path. Because the count is a pure
 * function of (elapsed, period, offset), any two synced emitters with the same
 * gridMultiple and offset fire on the same steps, and changing tempo (which
 * changes gridSec) makes the period follow the grid with no explicit re-anchor.
 */
export function computeGridEmitCount(
  gridSec: number,
  gridMultiple: number,
  offset: number,
  tPrev: number,
  tNow: number,
): number {
  return computeEmitCount(1 / (gridMultiple * gridSec), offset, tPrev, tNow);
}

/**
 * Sequencer emit count for an emitter that fires only on the active cells of its
 * pattern. The pattern holds `num` eighth-note cells (cell index 0…num-1), and
 * the global cell counter `g = floor(t / cellSec)` maps to pattern cell
 * `g mod num`, so the pattern loops every `num` cells and stays phase-locked to
 * the shared clock (cell 0 = clock origin). Emitters with different `num` loop
 * at different lengths over the same cell grid (polymeter). Returns one particle
 * per active-cell boundary crossed in (tPrev, tNow]. The look-back is clamped to
 * one pattern so a tempo change (which shrinks cellSec) cannot replay many
 * patterns of steps in a single fixed substep.
 */
export function computeSeqEmit(
  cellSec: number,
  num: number,
  activeSteps: readonly number[],
  tPrev: number,
  tNow: number,
): number {
  if (num <= 0 || activeSteps.length === 0) {
    return 0;
  }
  const gNow = Math.floor(tNow / cellSec);
  const gPrevRaw = Math.floor(tPrev / cellSec);
  const gPrev = Math.max(gPrevRaw, gNow - num);
  let count = 0;
  for (let g = gPrev + 1; g <= gNow; g++) {
    const step = ((g % num) + num) % num;
    if (activeSteps.includes(step)) {
      count++;
    }
  }
  return count;
}
